import { request } from '@mairistem/tools';
import React from 'react';
import { toast } from 'react-toastify';

import { postRecu, postRecuComptant, printRecu } from '@/routes';
import type { RecuEncaissementType } from '@/types';

interface EncaisseDepasseeType {
  encaisseGlobaleDepassee?: boolean;
  encaisseNumeraireDepassee?: boolean;
}

type SaveEncaissementType = (
  body: Record<string, unknown>,
  imprimerRecu?: boolean,
  envoyerMail?: boolean,
) => Promise<RecuEncaissementType>;

interface UseEncaissementResult {
  saveEncaissement: SaveEncaissementType;
  saveEncaissementComptant: SaveEncaissementType;
  loading: boolean;
  encaisseDepassee: EncaisseDepasseeType | null;
  resetEncaisseDepassee: () => void;
}

/**
 * Hook pour gérer la logique d'encaissement.
 * Encapsule l'appel API de création de reçu, la gestion des erreurs 422
 * (encaisse dépassée) et l'impression du reçu.
 */
const useEncaissement = (): UseEncaissementResult => {
  const [loading, setLoading] = React.useState(false);
  const [encaisseDepassee, setEncaisseDepassee] = React.useState<EncaisseDepasseeType | null>(null);

  const resetEncaisseDepassee = React.useCallback(() => setEncaisseDepassee(null), []);

  const printRecuPdf = React.useCallback(async (identifiantRecu: number, mail = false) => {
    try {
      const response: any = await request.get(printRecu(identifiantRecu, mail), {
        headers: { Accept: 'application/json, application/pdf' },
      });

      // Si la réponse contient une URL temporaire (JSON)
      if (response.url) {
        window.open(response.url, '_blank');
      } else {
        // Sinon, traiter comme un blob PDF (réponse directe)
        const blob = response.data || response;
        const pdfBlob = new Blob([blob], { type: 'application/pdf' });
        const url = URL.createObjectURL(pdfBlob);

        window.open(url, '_blank');

        // Nettoyer après un délai
        setTimeout(() => {
          URL.revokeObjectURL(url);
        }, 2000);
      }
    } catch {
      toast(
        mail ? "Erreur lors de l'envoi du reçu par mail" : "Erreur lors de l'impression du reçu",
        {
          type: 'error',
        },
      );
    }
  }, []);

  /**
   * Poste le reçu sur l'URL fournie puis imprime / envoie le reçu selon la demande.
   * Le traitement est identique pour un encaissement de facture et au comptant : seule
   * l'URL change, l'API du comptant créant en plus la facture et ses lignes.
   */
  const postEncaissement = React.useCallback(
    async (
      url: string,
      body: Record<string, unknown>,
      imprimerRecu = false,
      envoyerMail = false,
    ): Promise<RecuEncaissementType> => {
      setLoading(true);
      let recu: RecuEncaissementType = null;

      try {
        recu = await request.post(url, body);
      } catch (error: any) {
        // Gestion de l'erreur 422 pour l'encaisse dépassée
        if (error?.code === 422) {
          const errorData = error?.message?.errors || {};

          if (errorData.encaisseGlobaleDepassee || errorData.encaisseNumeraireDepassee) {
            setEncaisseDepassee({
              encaisseGlobaleDepassee: !!errorData.encaisseGlobaleDepassee,
              encaisseNumeraireDepassee: !!errorData.encaisseNumeraireDepassee,
            });
          }
        }

        setLoading(false);
        throw error;
      }

      if (recu && imprimerRecu) {
        await printRecuPdf(recu.identifiant);
      } else if (recu && envoyerMail) {
        await printRecuPdf(recu.identifiant, true);
      }

      setLoading(false);
      return recu;
    },
    [printRecuPdf],
  );

  const saveEncaissement = React.useCallback(
    (
      body: Record<string, unknown>,
      imprimerRecu = false,
      envoyerMail = false,
    ): Promise<RecuEncaissementType> =>
      postEncaissement(postRecu(), body, imprimerRecu, envoyerMail),
    [postEncaissement],
  );

  const saveEncaissementComptant = React.useCallback(
    (
      body: Record<string, unknown>,
      imprimerRecu = false,
      envoyerMail = false,
    ): Promise<RecuEncaissementType> =>
      postEncaissement(postRecuComptant(), body, imprimerRecu, envoyerMail),
    [postEncaissement],
  );

  return {
    saveEncaissement,
    saveEncaissementComptant,
    loading,
    encaisseDepassee,
    resetEncaisseDepassee,
  };
};

export default useEncaissement;
